import Header from '@/components/Header';
import HeroSection from '@/components/HeroSection';
import RestaurantCategories from '@/components/RestaurantCategories';
import StoresGrid from '@/components/RestaurantsGrid';
import LumzaLogo from '@/components/LumzaLogo';
import FloatingActionButton from '@/components/FloatingActionButton';
import WhatsAppSupport from '@/components/WhatsAppSupport';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { MapPin, Phone, Mail, Instagram, Facebook, Twitter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useNavigate } from 'react-router-dom';
const Index = () => {
  const {
    t
  } = useLanguage();
  const {
    getCartCount
  } = useCart();
  const navigate = useNavigate();
  const homeJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'LUMZA',
      url: 'https://lumzadelivery.com',
      logo: 'https://storage.googleapis.com/gpt-engineer-file-uploads/8SGDJTPUXmOdhzNK0V2xANcbrlD2/uploads/1762443837721-LUMZA 1.1.png',
      sameAs: [],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+27-62-581-9719',
        contactType: 'customer support',
        areaServed: 'ZA',
        availableLanguage: ['English', 'Zulu', 'Xhosa']
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'LUMZA Delivery',
      url: 'https://lumzadelivery.com',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://lumzadelivery.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string'
      }
    }
  ];
  return <div className="min-h-screen bg-background">
      <SEO
        title="LUMZA Delivery — After-Hours Food & Essentials in SA"
        description="Order food, groceries, meds and beverages between 11PM–7AM in South Africa. Fast, safe after-hours delivery from nearby stores."
        path="/"
        jsonLd={homeJsonLd}
      />
      <Header />
      
      <main>
        <HeroSection />
        <StoresGrid />
      </main>

      {/* WhatsApp Floating Button - Draggable */}
      

      {/* Mobile Floating Action Button */}
      <FloatingActionButton cartItems={getCartCount()} onCartClick={() => navigate('/checkout')} />

      {/* Footer */}
      <footer className="bg-dark-surface border-t border-border/40 py-6 sm:py-8 md:py-12 lg:py-16">
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Brand */}
            <div className="space-y-3 sm:space-y-4 col-span-2 lg:col-span-1">
              <Link to="/">
                <LumzaLogo size="md" />
              </Link>
              <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed max-w-xs">
                {t('footer.tagline')}
              </p>
              <div className="flex space-x-2">
                <Button size="sm" variant="ghost" className="p-2 hover:bg-primary/10 touch-target" aria-label="Follow us on Instagram">
                  <Instagram className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="p-2 hover:bg-primary/10 touch-target" aria-label="Follow us on Facebook">
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="p-2 hover:bg-primary/10 touch-target" aria-label="Follow us on Twitter">
                  <Twitter className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-3 sm:space-y-4">
              <h4 className="font-semibold text-sm sm:text-base text-foreground">{t('footer.quickLinks')}</h4>
              <nav className="flex flex-col space-y-2 text-xs sm:text-sm">
                <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">
                  About LUMZA
                </Link>
                <Link to="/help" className="text-muted-foreground hover:text-primary transition-colors">
                  {t('footer.helpCenter')}
                </Link>
                <Link to="/onboard/vendor" className="text-muted-foreground hover:text-primary transition-colors">
                  {t('footer.becomePartner')}
                </Link>
                <Link to="/onboard/driver" className="text-muted-foreground hover:text-primary transition-colors">
                  {t('footer.driverSignUp')}
                </Link>
                <Link to="/install" className="text-muted-foreground hover:text-primary transition-colors">
                  Install App
                </Link>
              </nav>
            </div>

            {/* Languages */}
            <div className="space-y-3 sm:space-y-4">
              <h4 className="font-semibold text-sm sm:text-base text-foreground">{t('footer.languages')}</h4>
              <div className="flex flex-col space-y-2 text-xs sm:text-sm">
                <button className="text-left text-muted-foreground hover:text-primary transition-colors">
                  🇬🇧 English
                </button>
                <button className="text-left text-muted-foreground hover:text-primary transition-colors">
                  🇿🇦 isiZulu
                </button>
                <button className="text-left text-muted-foreground hover:text-primary transition-colors">
                  🇿🇦 isiXhosa
                </button>
                <button className="text-left text-muted-foreground hover:text-primary transition-colors">
                  🇨🇳 中文
                </button>
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-3 sm:space-y-4">
              <h4 className="font-semibold text-sm sm:text-base text-foreground">{t('footer.contact')}</h4>
              <div className="flex flex-col space-y-2 text-xs sm:text-sm">
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 flex-shrink-0" />
                  <span>Sandton, South Africa</span>
                </div>
                <a href="https://wa.me/27625819719" className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <span>+27 62 581 9719</span>
                </a>
                <a href="mailto:hello@lumzadelivery.com" className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <span>hello@lumzadelivery.com</span>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6 sm:mt-8 md:mt-10 lg:mt-12 pt-4 sm:pt-6 md:pt-8 border-t border-border/40 flex flex-row flex-wrap justify-center md:justify-between items-center gap-x-3 gap-y-1 text-[10px] sm:text-xs md:text-sm text-muted-foreground">
            <p className="whitespace-nowrap">{t('footer.copyright')}</p>
            <div className="flex flex-row items-center gap-x-3 whitespace-nowrap">
              <Link to="/legal" className="hover:text-primary transition-colors">Legal Hub</Link>
              <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy</Link>
              <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
};
export default Index;